/** A cycle on its chart: the price, the ladder, the average and the exit. */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/design-system/Button";
import { slotQuote } from "@/shared/cycles/quote";
import { readSlotCandles, type SlotCandlesResponse, type SlotResponse } from "@/shared/api";

import {
  buildCycleChartOption,
  cycleLines,
  spreadTags,
  type CycleMarks,
  type CyclePalette,
} from "./cycleChartOption";

type ChartHandle = {
  readonly setOption: (option: object, notMerge?: boolean) => void;
  readonly convertToPixel: (finder: { yAxisIndex: number }, value: number) => number;
  readonly resize: () => void;
  readonly dispose: () => void;
};

function paletteFromDocument(): CyclePalette {
  const style = getComputedStyle(document.documentElement);
  const token = (name: string): string => style.getPropertyValue(name).trim();
  return {
    background: token("--color-surface-default"),
    border: token("--color-border-strong"),
    grid: token("--color-chart-grid"),
    fontMono: token("--font-mono"),
    fontSans: token("--font-sans"),
    negative: token("--color-chart-negative"),
    positive: token("--color-chart-positive"),
    info: token("--color-status-info"),
    warning: token("--color-status-warning"),
    text: token("--color-text-primary"),
    muted: token("--color-text-muted"),
  };
}

export function marksOf(slot: SlotResponse, simple = false): CycleMarks | null {
  if (slot.opened_at === null) return null;
  return {
    openedAt: slot.opened_at,
    // A client sees the entry, the average and the exit, not the ladder.
    levels: simple ? [] : (slot.evidence?.placement?.levels ?? []),
    average: slot.vwap ?? null,
    target: slot.outlook?.target_price ?? slot.exit_arm_price ?? slot.exit_net_floor_price ?? null,
    trigger: slot.cycle_phase === "TRAILING" ? (slot.trailing_exit_trigger_price ?? null) : null,
    current: slot.current_price ?? null,
  };
}

const LEGEND: ReadonlyArray<readonly [string, string]> = [
  ["--color-text-primary", "Средняя цена"],
  ["--color-chart-positive", "Цель выхода"],
  ["--color-text-muted", "Ордера сетки: сплошные куплены, пунктир ждут"],
  ["--color-status-warning", "Трейлинг"],
  ["--color-status-info", "Цена сейчас"],
];

const SIMPLE_LEGEND: ReadonlyArray<readonly [string, string]> = [
  ["--color-text-primary", "Средняя цена покупки"],
  ["--color-chart-positive", "Цена, с которой бот начнёт фиксировать прибыль"],
  ["--color-status-info", "Цена сейчас"],
];

/** The bar lengths a reader may choose, null for the one the span suggests. */
const INTERVALS: ReadonlyArray<readonly [number | null, string]> = [
  [null, "Авто"],
  [15, "15м"],
  [60, "1ч"],
  [240, "4ч"],
  [1440, "1д"],
];

type Tag = { readonly key: string; readonly text: string; readonly color: string; readonly top: number };

/** Below this width the axis tags carry the price alone. */
const COMPACT_BELOW = 560;

export function CycleChart({ slot, simple = false, large = false }: {
  readonly slot: SlotResponse;
  /** The client's view: no ladder, a shorter legend. */
  readonly simple?: boolean;
  /** The terminal's main chart: taller, with a choice of bar length. */
  readonly large?: boolean;
}): React.JSX.Element | null {
  const marks = marksOf(slot, simple);
  const [interval, chooseInterval] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["slot-candles", slot.slot_id, slot.opened_at, interval],
    enabled: marks !== null,
    queryFn: async (): Promise<SlotCandlesResponse> => {
      const { data, error } = await readSlotCandles({
        path: { slot_id: slot.slot_id },
        query: interval === null ? {} : { interval },
      });
      if (error !== undefined || data === undefined) throw new Error("candles unreadable");
      return data;
    },
    refetchInterval: 60_000,
    retry: false,
  });
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartHandle | null>(null);
  const latest = useRef<{ bars: SlotCandlesResponse["bars"]; marks: CycleMarks } | null>(null);
  latest.current = query.data === undefined || marks === null ? null : { bars: query.data.bars, marks };

  const [tags, setTags] = useState<readonly Tag[]>([]);
  const draw = (): void => {
    const state = latest.current;
    const host = hostRef.current;
    const chart = chartRef.current;
    if (state === null || host === null || chart === null) return;
    const palette = paletteFromDocument();
    const compact = host.clientWidth < COMPACT_BELOW;
    chart.setOption(buildCycleChartOption(state.bars, state.marks, palette, compact), true);
    // The tags go where the chart drew each price, then step clear of each other.
    const named = cycleLines(state.marks, palette).filter((line) => line.named);
    const quoteOf = slotQuote(slot);
    const pixels = named.map((line) => chart.convertToPixel({ yAxisIndex: 0 }, line.value));
    const placed = spreadTags(pixels);
    setTags(
      named.map((line, index) => ({
        key: line.label,
        text: compact ? quoteOf(line.text) : `${line.label} ${quoteOf(line.text)}`,
        color: line.color,
        top: placed[index] ?? 0,
      })),
    );
  };
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === "undefined") return undefined;
    let cancelled = false;
    let resize: ResizeObserver | null = null;
    let theme: MutationObserver | null = null;
    void import("@/shared/analytics/chartEngine").then(({ initialiseChart }) => {
      if (cancelled) return;
      chartRef.current = initialiseChart(host);
      drawRef.current();
      resize = new ResizeObserver(() => {
        chartRef.current?.resize();
        drawRef.current();
      });
      resize.observe(host);
      theme = new MutationObserver(() => {
        drawRef.current();
      });
      theme.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    });
    return () => {
      cancelled = true;
      resize?.disconnect();
      theme?.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  useEffect(() => {
    drawRef.current();
  }, [query.data, slot]);

  if (marks === null) return null;
  return (
    <div className="min-w-0 sm:col-span-2" data-testid={`cycle-chart-${slot.slot_id}`}>
      {large && (
        <div role="group" aria-label="Длина свечи" className="mb-2 flex flex-wrap gap-1">
          {INTERVALS.map(([value, label]) => (
            <Button
              key={label}
              variant="ghost"
              size="sm"
              selected={interval === value}
              aria-pressed={interval === value}
              onClick={() => {
                chooseInterval(value);
              }}
            >
              {label}
            </Button>
          ))}
        </div>
      )}
      <div className="relative">
        <div
          ref={hostRef}
          className={`w-full ${large ? "h-[26rem] max-lg:h-[60vh]" : "h-64"}`}
          role="img"
          aria-label={`График ${slot.symbol ?? ""}: цена, ордера сетки, средняя цена и цель выхода`}
        />
        {tags.map((tag) => (
          <span
            key={tag.key}
            className="pointer-events-none absolute right-1 -translate-y-1/2 rounded-sm px-1 font-numeric text-[11px] leading-4 whitespace-nowrap text-background-base"
            style={{ top: `${tag.top.toFixed(1)}px`, background: tag.color }}
            data-testid={`chart-tag-${tag.key}`}
          >
            {tag.text}
          </span>
        ))}
      </div>
      {query.isError && (
        <p className="text-helper-sm text-status-warning">График сейчас не загрузился, попробую снова через минуту.</p>
      )}
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-helper-sm text-text-secondary">
        {(simple ? SIMPLE_LEGEND : LEGEND).map(([swatch, label]) => (
          <li key={label} className="flex items-center gap-1">
            <span aria-hidden="true" className="inline-block h-0.5 w-3" style={{ background: `var(${swatch})` }} />
            {label}
          </li>
        ))}
      </ul>
    </div>
  );
}
